import { Injectable } from '@angular/core';
import { ThemeService } from './theme.service';
import { TypographyManagementService } from './typography-management.service';
import { ThemeEventBus } from './theme-event-bus.service';
import { CpCompanyService } from 'src/app/admin/tf-admin/tf-configuration/tf-company/service/cp-company.service';
import { SocialMediaService } from './social-media.service';
import { AdAdminService } from 'src/app/admin/ad-admin/ad-admin.service';
import { FooterStore } from './footer-store.service';
import { forkJoin, of, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AppInitializerService {
  constructor(
    private themeService: ThemeService,
    private typographyManagementService: TypographyManagementService,
    private eventBus: ThemeEventBus,
    private cpCompanyService: CpCompanyService,
    private socialMediaService: SocialMediaService,
    private adAdminService: AdAdminService,
    private footerStore: FooterStore
  ) {}

  /**
   * Initialize the application by loading complete theme (colors + button configs) and footer data
   */
  async initializeApp(): Promise<void> {
    try {
      console.log('🚀 AppInitializerService: Initializing app with complete theme (colors + button configs) and footer data...');
      
      // Listen for theme events during initialization
      this.setupEventListeners();
      
      // Load theme (colors + buttons), typography, and footer data in parallel
      const [completeTheme, typographyState] = await Promise.all([
        this.themeService.getCompleteTheme().toPromise(),
        this.typographyManagementService.getTypographyData().toPromise(),
        this.loadFooterData()
      ]);
      
      console.log('✅ AppInitializerService: Complete theme loaded successfully:', completeTheme);
      console.log('✅ AppInitializerService: Typography loaded successfully:', typographyState);
      console.log('✅ AppInitializerService: Footer data loaded successfully');
      console.log('🎨 AppInitializerService: CSS variables should now be updated with backend values!');
    } catch (error) {
      console.error('❌ AppInitializerService: Error initializing app:', error);
      // App will continue with default colors and footer data
    }
  }

  /**
   * Load footer data and company data during initialization
   * - Company data: single logo (for nav) + company name + tagline (from CP/cp_companyRetrive API)
   * - Footer data: multiple support logos (from AD/supportlogos/ API) + social media links
   */
  private async loadFooterData(): Promise<void> {
    try {
      console.log('📋 AppInitializerService: Loading company and footer data...');
      this.footerStore.setLoading(true);

      // Load all data in parallel
      const footerData$ = forkJoin({
        // Company API: provides single logo for nav, company name, and tagline for footer
        company: this.cpCompanyService.getCompany().pipe(
          catchError(error => {
            console.error('❌ AppInitializerService: Error loading company data:', error);
            return of(null);
          })
        ),
        // Social media links for footer
        socialLinks: this.adAdminService.getSocialMediaLinks().pipe(
          catchError(error => {
            console.error('❌ AppInitializerService: Error loading social media links:', error);
            return of([]);
          })
        ),
        // Support logos for footer (different from company logo - multiple logos from AD/supportlogos/)
        supportLogos: this.adAdminService.getSupportLogos().pipe(
          catchError(error => {
            console.error('❌ AppInitializerService: Error loading support logos:', error);
            return of([]);
          })
        )
      });

      const data = await firstValueFrom(footerData$);

      // Process company data (single logo for nav + favicon for tab + company name + tagline for footer)
      if (data.company) {
        const logoUrl = data.company.logo_url || 'assets/images/logo.png';
        const favIconUrl = data.company.favIcon || null;
        const tagLine = data.company.tagLine || '';
        const companyName = data.company.name || '';
        
        // Update CpCompanyService so components (NavLogoComponent) will get the data immediately
        // This is the SINGLE logo used in the navigation bar
        this.cpCompanyService.updateLogoUrl(logoUrl);
        // Favicon for browser tab - applied app-wide like logo
        if (favIconUrl) {
          this.cpCompanyService.updateFavIconUrl(favIconUrl);
        }
        if (companyName) {
          this.cpCompanyService.updateComanyName(companyName);
        }
        
        // Update footer store with tagline (logoUrl here is fallback if no support logos)
        this.footerStore.updateData({
          logoUrl,
          tagLine
        });
        
        console.log('✅ AppInitializerService: Company data loaded - Nav Logo:', logoUrl, 'Favicon:', favIconUrl || '(default)', 'Tagline:', tagLine, 'Company Name:', companyName);
      }

      // Process social media links
      const socialLinks = this.extractSocialMediaLinks(data.socialLinks);
      this.socialMediaService.setLinks(socialLinks);
      this.footerStore.updateData({ socialLinks });
      console.log('✅ AppInitializerService: Social media links loaded:', socialLinks.length);

      // Process support logos (multiple logos for footer from AD/supportlogos/ API)
      // These are different from the company logo - footer can display multiple support logos
      const supportLogos = this.extractSupportLogos(data.supportLogos);
      this.footerStore.updateData({ supportLogos });
      console.log('✅ AppInitializerService: Footer support logos loaded (from AD/supportlogos/):', supportLogos.length);

      this.footerStore.setLoading(false);
    } catch (error) {
      console.error('❌ AppInitializerService: Error loading footer data:', error);
      this.footerStore.setError('Failed to load footer data');
      this.footerStore.setLoading(false);
    }
  }

  /**
   * Extract social media links from API response
   */
  private extractSocialMediaLinks(data: any): Array<{ name: string; link: string }> {
    if (!data) { return []; }
    const arr = Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data)
          ? data.data
          : [];
    return arr
      .map((x: any) => ({
        name: (x.name || x.platform || x.platform_name || '').toString(),
        link: (x.link || x.url || '').toString()
      }))
      .filter(x => !!x.name && !!x.link);
  }

  /**
   * Extract support logos from API response
   */
  private extractSupportLogos(data: any): Array<{ url: string; link: string }> {
    if (!data) { return []; }
    const arr = Array.isArray(data) ? data : (data?.results || data?.data || []);
    return (arr || []).map((it: any) => ({
      url: it.logo || it.logo_url || '',
      link: it.logo_link || '#'
    })).filter(x => !!x.url);
  }

  /**
   * Setup event listeners for initialization monitoring
   */
  private setupEventListeners(): void {
    this.eventBus.onThemeLoaded().subscribe(event => {
      console.log('🎉 AppInitializerService: Theme loaded successfully during initialization');
    });

    this.eventBus.onThemeError().subscribe(event => {
      console.log('⚠️ AppInitializerService: Theme error during initialization, using fallback colors');
    });
  }

  /**
   * Manual initialization for testing
   */
  async manualInitialize(): Promise<void> {
    console.log('🔧 AppInitializerService: Manual initialization triggered');
    await this.initializeApp();
  }
}
